import React, { useState, useEffect } from 'react';
import { Save, Package, Tag, Tags, FileText, Sparkles, Zap, Edit } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { MultiCategorySelect } from '../../components/common/MultiCategorySelect';
import { MultiImageUpload } from '../../components/common/MultiImageUpload';
import { Toast } from '../../components/common/Toast';
import { QRCodeModal } from '../../components/common/QRCodeModal';
import { DynamicAIFields } from '../../components/products/DynamicAIFields';
import { productsApi, aiApi, categoriesApi, aiAttributesApi } from '../../services/api';
import { CreateProductRequest, ProductImage, AIExecuteResponse, AttributeType, AIAttributeDefinition } from '../../types';
import { productFieldOptions, ProductFormData } from '../../config/productFields';
import { ensureCategoriesExist } from '../../utils/categoryHelpers';
import { mapAIAttributesToSquare, normalizeAttributeKey } from '../../utils/attributeMapping';
import { useAuth } from '../../hooks/useAuth';

interface ProductAddEditProps {
  productId?: string; // undefined = add mode, string = edit mode
  onNavigate: (page: string) => void;
  viewOnly?: boolean; // true = read-only view mode
}

export const ProductAddEdit: React.FC<ProductAddEditProps> = ({ productId, onNavigate, viewOnly = false }) => {
  console.log('%c🚀 ProductAddEdit v0.0.35 Loading', 'background: #222; color: #bada55; font-size: 16px; font-weight: bold;');

  const isEditMode = !!productId;
  const { organization } = useAuth();

  console.log('🔍 Initial organization check:', {
    hasOrganization: !!organization,
    orgId: organization?.id,
    orgName: organization?.name
  });

  // Removed unused attributes state
  const [loading, setLoading] = useState(isEditMode);
  
  // AI Attributes from user's organization
  const [aiAttributeDefinitions, setAiAttributeDefinitions] = useState<AIAttributeDefinition[]>([]);
  const [aiAttributesLoading, setAiAttributesLoading] = useState(false);
  
  // Mock data for development
  const [mockDataFile, setMockDataFile] = useState<File | null>(null);
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Get min required images from organization (defaults to 3)
  const minRequiredImages = organization?.min_required_images ?? 3;

  // Helper to create image slots based on count
  // Images are ordered by their position in the array (0 = primary)
  const createImageSlots = (count: number): ProductImage[] => {
    const slots: ProductImage[] = [];
    for (let i = 0; i < count; i++) {
      slots.push({
        id: String(i + 1),
        url: '',
        type: 'image',
        label: `Image ${i + 1}`,
        order: i // Track position: 0 = primary image
      });
    }
    return slots;
  };

  // Image handling - initialize with the required number of slots
  const [images, setImages] = useState<ProductImage[]>(() => createImageSlots(minRequiredImages));
  
  // AI functionality
  const [analyzing, setAnalyzing] = useState(false);
  const [aiExecuteResult, setAiExecuteResult] = useState<AIExecuteResponse | null>(null);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);

  // Fun loading messages for AI analysis (100+ items)
  const loadingMessages = [
    "🔍 Examining fabric patterns...",
    "🎨 Identifying colors and textures...", 
    "📏 Measuring sleeve lengths...",
    "🌸 Spotting cherry blossom motifs...",
    "🦋 Chasing silk threads...",
    "🌅 Dating historical periods...",
    "🎭 Decoding ceremonial significance...",
    "🧵 Following weaving patterns...",
    "🏮 Discovering regional styles...",
    "✨ Analyzing craftsmanship details...",
    "🎯 Cataloging embroidery techniques...",
    "🌿 Identifying seasonal motifs...",
    "🎪 Interpreting formality levels...",
    "🦢 Admiring elegant draping...",
    "🌊 Swimming through silk waves...",
    "🐕 Running after squirrels...",
    "🌷 Planting digital flowers...",
    "🦆 Counting fabric ducks...",
    "🎨 Mixing virtual paint...",
    "🐱 Herding pixel cats...",
    "🌙 Reading ancient textile wisdom...",
    "🎯 Teaching AI about fashion...",
    "🦄 Consulting unicorn stylists...",
    "🌺 Watering silk gardens...",
    "🎪 Juggling color swatches...",
    "🐛 Debugging fabric algorithms...",
    "🌈 Chasing rainbow threads...",
    "🦋 Interviewing butterfly experts...",
    "🌸 Having tea with cherry blossoms...",
    "🎭 Rehearsing with silk actors...",
    "🌀 Spinning digital looms...",
    "🎨 Painting with light pixels...",
    "🦢 Consulting swan fashion advisors...",
    "🌊 Surfing textile waves...",
    "🎪 Performing fabric acrobatics...",
    "🌙 Translating moon patterns...",
    "🦋 Butterfly whispering secrets...",
    "🌸 Cherry blossom meditation...",
    "🎯 Archery with color arrows...",
    "🌺 Collecting digital petals...",
    "🎭 Method acting as silk...",
    "🦄 Unicorn pattern consulting...",
    "🌈 Building rainbow bridges...",
    "🎪 Tightrope walking on threads...",
    "🌊 Deep sea thread diving...",
    "🦋 Butterfly migration tracking...",
    "🌸 Seasonal bloom prediction...",
    "🎨 Abstract art interpretation...",
    "🌙 Lunar cycle fabric reading...",
    "🦢 Swan song pattern analysis...",
    "🌺 Tropical fabric expedition...",
    "🎯 Precision color targeting...",
    "🌈 Spectrum frequency analysis...",
    "🎭 Dramatic textile interpretation...",
    "🦄 Mythical pattern discovery...",
    "🌊 Ocean current thread flow...",
    "🌸 Zen garden silk arrangement...",
    "🎪 Circus tent fabric inspection...",
    "🦋 Metamorphosis pattern study...",
    "🌙 Midnight textile mysteries...",
    "🌺 Exotic bloom classification...",
    "🎨 Impressionist color mixing...",
    "🦢 Graceful drape choreography...",
    "🌈 Prismatic thread refraction...",
    "🎯 Bullseye pattern precision...",
    "🌊 Tidal silk movement...",
    "🎭 Costume drama analysis...",
    "🦄 Magical thread enchantment...",
    "🌸 Sakura season prediction...",
    "🎪 Big top fabric engineering...",
    "🦋 Wing pattern geometry...",
    "🌙 Nocturnal weave secrets...",
    "🌺 Botanical textile study...",
    "🎨 Color theory application...",
    "🦢 Elegant pose analysis...",
    "🌈 Light spectrum reading...",
    "🎯 Thread trajectory mapping...",
    "🌊 Fluid dynamics modeling...",
    "🎭 Character costume design...",
    "🦄 Fantasy textile creation...",
    "🌸 Petal arrangement study...",
    "🎪 Performance fabric testing...",
    "🦋 Flight pattern analysis...",
    "🌙 Starlight thread weaving...",
    "🌺 Floral motif interpretation...",
    "🎨 Artistic vision processing...",
    "🦢 Ballet pose documentation...",
    "🌈 Color harmony calculation...",
    "🎯 Precision measurement taking...",
    "🌊 Water reflection analysis...",
    "🎭 Scene setting preparation...",
    "🦄 Dream fabric weaving...",
    "🌸 Spring awakening sensing...",
    "🎪 Show preparation timing...",
    "🦋 Delicate touch simulation...",
    "🌙 Evening wear assessment...",
    "🌺 Garden party planning...",
    "🎨 Canvas preparation work...",
    "🦢 Graceful movement tracking...",
    "🌈 Full spectrum analysis...",
    "🎯 Target accuracy checking...",
    "🌊 Depth perception testing...",
    "🎭 Final rehearsal review...",
    "🦄 Magic spell completion...",
    "🌸 Perfect timing awaiting...",
    "🎪 Grand finale approaching..."
  ];
  

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    sku: '',
    price: '',
    inventory_quantity: '1',
    category_id: undefined,
    categories: [],
    aiFields: {
      product_title: '',
      market_description: '',
      detailed_inventory_description: '',
      // Other fields will be added dynamically
    } as any,
    attributes: {}
  });

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [rawAttributes, setRawAttributes] = useState<Record<string, string | string[]>>({});
  const [rawAttributeArray, setRawAttributeArray] = useState<Array<{ name?: string; value?: string | string[]; definition_id?: string }>>([]);
  // QR code modal state - shows after first save (new product creation)
  const [showQRAfterSave, setShowQRAfterSave] = useState<{ productName: string; productId: string } | null>(null);

  useEffect(() => {
    console.log('🔄 ProductAddEdit mounted/updated', {
      hasOrganization: !!organization,
      organizationId: organization?.id,
      organizationName: organization?.name,
      productId
    });

    loadData();
    if (organization) {
      console.log('✅ Organization available, loading AI attributes...');
      loadAIAttributes();
    } else {
      console.warn('⚠️ No organization context available - AI attributes will not load');
    }
  }, [productId, organization]);

  // Re-initialize image slots when minRequiredImages changes (for new products only)
  useEffect(() => {
    if (!isEditMode && images.length !== minRequiredImages) {
      // Only reset if no images have been uploaded yet
      const hasUploadedImages = images.some(img => img.url);
      if (!hasUploadedImages) {
        setImages(createImageSlots(minRequiredImages));
      }
    }
  }, [minRequiredImages, isEditMode]);

  // Re-map attributes when AI definitions load
  useEffect(() => {
    if ((Object.keys(rawAttributes).length > 0 || rawAttributeArray.length > 0) && aiAttributeDefinitions && aiAttributeDefinitions.length > 0) {
      console.log('🔄 Re-mapping attributes with loaded definitions');
      console.log('📋 Raw attributes:', rawAttributes);
      console.log('📋 Raw attribute array:', rawAttributeArray);
      console.log('📋 AI attribute definitions:', aiAttributeDefinitions.map(d => ({ key: d.attribute_key, name: d.attribute_name })));

      const aiFields: Record<string, string> = {};

      // Special description fields that don't need definition mapping
      const specialFields = ['product_title', 'market_description', 'detailed_inventory_description'];

      // First, process rawAttributes (key-value pairs)
      Object.entries(rawAttributes).forEach(([key, value]) => {
        // Check if it's a special field (doesn't need definition mapping)
        if (specialFields.includes(key)) {
          aiFields[key] = Array.isArray(value) ? value.join(', ') : (value || '');
          console.log(`✅ Added special field ${key} = ${aiFields[key].substring(0, 50)}...`);
        } else {
          // Regular AI attribute - needs definition mapping
          const attrDef = aiAttributeDefinitions.find(d => d.attribute_key === key);
          if (attrDef) {
            aiFields[attrDef.attribute_key] = Array.isArray(value) ? value.join(', ') : (value || '');
            console.log(`✅ Re-mapped ${attrDef.attribute_key} = ${aiFields[attrDef.attribute_key]}`);
          }
        }
      });

      // Second, process rawAttributeArray (attributes with definition_id)
      rawAttributeArray.forEach(attr => {
        if (attr.name && attr.value !== undefined) {
          // Already processed in rawAttributes
          return;
        }
        if (attr.definition_id && attr.value !== undefined) {
          const definition = aiAttributeDefinitions.find(d => d.id === attr.definition_id);
          if (definition) {
            aiFields[definition.attribute_key] = Array.isArray(attr.value) ? attr.value.join(', ') : (attr.value || '');
            console.log(`✅ Mapped from definition_id ${attr.definition_id} → ${definition.attribute_key} = ${aiFields[definition.attribute_key]}`);
          } else {
            console.warn(`⚠️ No definition found for definition_id: ${attr.definition_id}`);
          }
        }
      });

      console.log('🔍 RE-MAPPING COMPLETE - aiFields being set:', {
        product_title: aiFields.product_title,
        market_description: aiFields.market_description?.substring(0, 50),
        detailed_inventory_description: aiFields.detailed_inventory_description?.substring(0, 50),
        totalKeys: Object.keys(aiFields).length,
        allKeys: Object.keys(aiFields)
      });

      // Update formData with mapped attributes
      setFormData(prev => {
        const updated = {
          ...prev,
          aiFields: {
            ...prev.aiFields,
            ...aiFields,
          }
        };
        console.log('🔍 FormData after re-mapping:', {
          aiFieldsKeys: Object.keys(updated.aiFields),
          product_title: updated.aiFields.product_title,
          market_description: updated.aiFields.market_description?.substring(0, 50),
          detailed_inventory_description: updated.aiFields.detailed_inventory_description?.substring(0, 50)
        });
        return updated;
      });
    }
  }, [rawAttributes, rawAttributeArray, aiAttributeDefinitions]);

  // Remove classification categories from additional categories
  // Only the main category field is used for classification now
  useEffect(() => {
    if (formData.categories.length === 0) return;
    if (!formData.aiFields.category) return;

    // Get the main classification category name
    const classificationCategoryName = formData.aiFields.category;

    // Find if any selected additional categories match the classification category
    categoriesApi.getAll().then(response => {
      if (response.success && response.data) {
        const classificationCat = response.data.find(cat => cat.name === classificationCategoryName);

        if (classificationCat && formData.categories.includes(classificationCat.id)) {
          console.log('Removing classification category from additional categories:', classificationCat.id);
          setFormData(prev => ({
            ...prev,
            categories: prev.categories.filter(id => id !== classificationCat.id)
          }));
        }
      }
    });
  }, [formData.aiFields.category, formData.categories.length]);

  const loadAIAttributes = async () => {
    if (!organization) {
      console.warn('⚠️ No organization available to load AI attributes');
      return;
    }

    console.log('🔄 Loading AI attributes for organization:', {
      id: organization.id,
      name: organization.name,
      display_name: organization.display_name
    });

    try {
      setAiAttributesLoading(true);
      const response = await aiAttributesApi.list({
        organization_id: organization.id, // Already a string UUID
        is_active: true
      });

      console.log('📡 AI attributes API response:', response);

      if (response.success && response.data) {
        setAiAttributeDefinitions(response.data);
        console.log(`✅ Loaded ${response.data.length} AI attribute definitions`);
        if (response.data.length > 0) {
          console.log('📋 Attribute keys:', response.data.map(d => d.attribute_key));
        }
      } else {
        console.error('❌ Failed to load AI attribute definitions:', response.message);
        console.log('Response details:', response);
      }
    } catch (error) {
      console.error('❌ Exception loading AI attributes:', error);
    } finally {
      setAiAttributesLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Note: We're using AI attributes now, not Square attributes
      // AI attributes are loaded separately in loadAIAttributes()

      // Load product data if in edit mode
      if (isEditMode && productId) {
        const productResponse = await productsApi.getById(productId);
        console.log('🔍 Product edit - API response:', productResponse);
        if (productResponse.success && productResponse.data) {
          const productData = productResponse.data;
          console.log('📦 Product data for editing:', {
            name: productData.name,
            sku: productData.sku,
            price_amount: productData.price_amount,
            description: productData.description?.substring(0, 100),
            categories: productData.categories,
            attributes: productData.attributes
          });
          console.log('🔍 DETAILED ATTRIBUTES FROM BACKEND:', JSON.stringify(productData.attributes, null, 2));
          
          // Load images
          if (productData.images && productData.images.length > 0) {
            console.log('📷 Loading images from productData.images:', productData.images);
            const loadedImages: ProductImage[] = productData.images.map((img: ProductImage, index: number) => {
              // Map primary/additional to front/back/detail for the form
              let mappedType: 'front' | 'back' | 'detail' = 'front';
              if (img.type === 'front' || img.type === 'back' || img.type === 'detail') {
                mappedType = img.type;
              } else {
                // Map primary/additional to front/back/detail based on index
                mappedType = index === 0 ? 'front' : index === 1 ? 'back' : 'detail';
              }

              return {
                id: img.id || `${index}`,
                url: img.url,
                type: mappedType,
                label: mappedType === 'front' ? 'Front Image' : mappedType === 'back' ? 'Back Image' : 'Detail Image',
              };
            });

            // Fill remaining slots with empty images
            while (loadedImages.length < 3) {
              const type = loadedImages.length === 0 ? 'front' : loadedImages.length === 1 ? 'back' : 'detail';
              loadedImages.push({
                id: `${loadedImages.length + 1}`,
                url: '',
                type: type,
                label: type === 'front' ? 'Front Image' : type === 'back' ? 'Back Image' : 'Detail Image',
              });
            }

            console.log('✅ Setting images:', loadedImages);
            setImages(loadedImages.slice(0, 3));
          }
          
          // Process attributes from backend
          // Backend returns attributes with keys from AI attribute definitions
          // We need to match them to the frontend attribute definition keys
          const aiFields: Record<string, string> = {};
          const regularAttributes: Record<string, string | string[]> = {};

          let attributeData: Record<string, string | string[]> = {};
          // Store raw attributes with definition_id for later mapping when definitions load
          const rawAttributeArray: Array<{ name?: string; value?: string | string[]; definition_id?: string }> = [];

          if (productData.attributes) {
            if (Array.isArray(productData.attributes)) {
              productData.attributes.forEach((attr: { name?: string; value?: string | string[]; definition_id?: string }) => {
                // Store raw attribute for later mapping
                rawAttributeArray.push(attr);

                if (attr.name && attr.value !== undefined) {
                  attributeData[attr.name] = attr.value;
                } else if (attr.definition_id) {
                  // Try to map now if definitions are loaded
                  const definition = aiAttributeDefinitions?.find(d => d.id === attr.definition_id);
                  if (definition && attr.value !== undefined) {
                    attributeData[definition.attribute_key] = attr.value;
                  }
                  // If definition not found yet, it will be mapped by useEffect when definitions load
                }
              });
            } else {
              attributeData = productData.attributes as Record<string, string | string[]>;
            }
          }

          console.log('📋 Processing attributes from backend:', attributeData);
          console.log('📋 AI attribute definitions available:', aiAttributeDefinitions?.length || 0);

          // Add special fields to attributeData so they get re-mapped properly
          const specialFieldsData = {
            ...attributeData,
            product_title: attributeData.product_title || productData.name || '',
            market_description: attributeData.market_description || productData.description || '',
            detailed_inventory_description: attributeData.detailed_inventory_description || '',
          };

          console.log('🔍 Special fields data after merge:', {
            product_title: specialFieldsData.product_title,
            market_description: specialFieldsData.market_description?.substring(0, 50),
            detailed_inventory_description: specialFieldsData.detailed_inventory_description?.substring(0, 50)
          });

          // Store raw attributes for later mapping when definitions load
          setRawAttributes(specialFieldsData);
          setRawAttributeArray(rawAttributeArray);

          // If definitions are already loaded, map immediately
          if (aiAttributeDefinitions && aiAttributeDefinitions.length > 0) {
            Object.entries(specialFieldsData).forEach(([key, value]) => {
              // Special fields that don't need definition mapping
              const specialFields = ['product_title', 'market_description', 'detailed_inventory_description'];
              if (specialFields.includes(key)) {
                aiFields[key] = Array.isArray(value) ? value.join(', ') : (value || '');
              } else {
                // Regular AI attribute - needs definition mapping
                const attrDef = aiAttributeDefinitions.find(d => d.attribute_key === key);
                if (attrDef) {
                  aiFields[attrDef.attribute_key] = Array.isArray(value) ? value.join(', ') : (value || '');
                } else {
                  regularAttributes[key] = value as string | string[];
                }
              }
            });
          }


          const initialCategories = productData.categories || (productData.category_id ? [productData.category_id] : []);

          // Use the aiFields directly - they already have the correct keys from attribute definitions
          // Just add the special fields (product_title, market_description, etc.) if they don't exist
          const finalAiFields = {
            ...aiFields,
            // Fallback to product data for fields that might not be in AI attributes
            product_title: aiFields.product_title || specialFieldsData.product_title || productData.name || '',
            market_description: aiFields.market_description || specialFieldsData.market_description || productData.description || '',
            detailed_inventory_description: aiFields.detailed_inventory_description || specialFieldsData.detailed_inventory_description || '',
          };

          const formDataToSet = {
            name: productData.name || '',
            description: productData.description || '',
            sku: productData.sku || '',
            price: productData.price_amount ? (productData.price_amount / 100).toString() : '',
            inventory_quantity: productData.inventory_quantity ? productData.inventory_quantity.toString() : '1',
            category_id: productData.category_id,
            categories: initialCategories,
            attributes: regularAttributes,
            aiFields: finalAiFields,
          };

          console.log('✅ Setting form data:', {
            name: formDataToSet.name,
            sku: formDataToSet.sku,
            price: formDataToSet.price,
            hasDescription: !!formDataToSet.description,
            categoriesCount: formDataToSet.categories.length,
            categories: formDataToSet.categories,
            aiFieldsKeys: Object.keys(formDataToSet.aiFields),
            aiFieldsSample: Object.entries(formDataToSet.aiFields).slice(0, 5)
          });

          console.log('🔍 SPECIAL FIELDS in finalAiFields:', {
            product_title: finalAiFields.product_title,
            market_description: finalAiFields.market_description?.substring(0, 50),
            detailed_inventory_description: finalAiFields.detailed_inventory_description?.substring(0, 50),
            totalKeys: Object.keys(finalAiFields).length
          });

          setFormData(formDataToSet);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setToast({ message: 'Error loading data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-extract SKU from image filenames
  const extractSkuFromImages = (images: ProductImage[]): string => {
    for (const image of images) {
      if (image.file && image.file.name) {
        const filename = image.file.name;
        const match = filename.match(/^([^_]+)_(?:back|detail|front)/i);
        if (match) {
          return match[1];
        }
        const baseName = filename.replace(/\.[^/.]+$/, '');
        const cleanName = baseName.replace(/_(back|detail|front)$/i, '');
        if (cleanName) {
          return cleanName;
        }
      }
    }
    return '';
  };

  useEffect(() => {
    if (!isEditMode) {
      const autoSku = extractSkuFromImages(images);
      if (autoSku && !formData.sku) {
        // Extract SKU prefix for category auto-population
        const skuPrefix = autoSku.match(/^[A-Z]+/)?.[0] || '';
        
        // Auto-populate category fields based on SKU prefix
        let categoryUpdate = {};
        if (skuPrefix && !formData.aiFields.category) {
          if (skuPrefix === 'H') {
            categoryUpdate = { category: 'Adult Haori', size_category: 'Adult' };
          } else if (skuPrefix === 'KW') {
            categoryUpdate = { category: 'Adult Kimono', gender: 'Women', size_category: 'Adult' };
          } else if (skuPrefix === 'KM') {
            categoryUpdate = { category: 'Adult Kimono', gender: 'Men', size_category: 'Adult' };
          } else if (skuPrefix === 'KC') {
            categoryUpdate = { category: 'Child Kimono', gender: 'Child_Unisex', size_category: 'Child' };
          }
        }
        
        setFormData(prev => ({
          ...prev,
          sku: autoSku,
          aiFields: {
            ...prev.aiFields,
            ...categoryUpdate
          }
        }));
        
        // Show user feedback for auto-population
        if (Object.keys(categoryUpdate).length > 0) {
          console.log(`Auto-populated category fields from SKU prefix "${skuPrefix}":`, categoryUpdate);
          setToast({ 
            message: `Auto-populated category fields from SKU prefix "${skuPrefix}"`, 
            type: 'success' 
          });
        }
      }
    }
  }, [images, isEditMode, formData.sku, formData.aiFields.category]);

  // Pick random loading messages during analysis
  useEffect(() => {
    if (analyzing) {
      const interval = setInterval(() => {
        setCurrentLoadingMessage(Math.floor(Math.random() * loadingMessages.length));
      }, 5000); // Change message every 5 seconds

      return () => clearInterval(interval);
    }
  }, [analyzing, loadingMessages.length]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (name.startsWith('aiFields.')) {
      const fieldName = name.replace('aiFields.', '');
      
      setFormData(prev => {
        const updatedAiFields = {
          ...prev.aiFields,
          [fieldName]: value
        };
        
        // Auto-populate size_category when category changes
        if (fieldName === 'category') {
          if (value.includes('Adult')) {
            updatedAiFields.size_category = 'Adult';
          } else if (value.includes('Child')) {
            updatedAiFields.size_category = 'Child';
          }
        }
        
        return {
          ...prev,
          aiFields: updatedAiFields
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // const handleAttributeChange = (attributeName: string, value: string | string[]) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     attributes: {
  //       ...prev.attributes,
  //       [attributeName]: value
  //     }
  //   }));
  // };

  /**
   * Apply organization's image ordering patterns to determine image order based on filename.
   * Images are matched against configured patterns (e.g., "_front", "_back") and assigned
   * the corresponding order value. Images that don't match any pattern keep their original order.
   */
  const applyImageOrderingPatterns = (imgs: any[]): any[] => {
    const patterns = organization?.image_ordering_patterns || [];

    if (patterns.length === 0) {
      // No patterns configured - keep original order
      return imgs;
    }

    return imgs.map((img, index) => {
      // Get the filename to check against patterns
      const filename = img.file?.name || img.filename || img.original_name || '';

      // Try to match against configured patterns
      let matchedOrder: number | undefined;
      for (const pattern of patterns) {
        if (filename.toLowerCase().includes(pattern.pattern.toLowerCase())) {
          matchedOrder = pattern.order;
          console.log(`📌 Image "${filename}" matched pattern "${pattern.pattern}" → order ${pattern.order}`);
          break;
        }
      }

      // Use matched order if found, otherwise keep the slot's index as order
      return {
        ...img,
        order: matchedOrder !== undefined ? matchedOrder : index
      };
    });
  };

  const handleImagesChange = (newImages: any[]) => {
    // Apply filename-based ordering patterns if configured
    const imagesWithPatternOrder = applyImageOrderingPatterns(newImages);

    // Convert ImageSlot[] to ProductImage[]
    const productImages: ProductImage[] = imagesWithPatternOrder.map(img => ({
      id: img.id,
      url: img.url,
      type: img.type as 'front' | 'back' | 'detail' | 'primary' | 'additional',
      label: img.label,
      file: img.file,
      order: img.order, // Order determined by pattern matching or upload position
      needsUrlResolution: img.needsUrlResolution,
      // Preserve temporary file metadata from Google Drive imports
      temp_path: img.temp_path,
      drive_file_id: img.drive_file_id,
      original_name: img.original_name,
      filename: img.filename
    }));

    console.log('📸 Images after pattern-based ordering:',
      productImages.map(img => ({
        filename: img.file?.name || img.filename,
        order: img.order
      }))
    );

    setImages(productImages);
  };

  // Handle mock file upload
  const handleMockFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/json') {
      setMockDataFile(file);
      setToast({ message: 'Mock data file loaded', type: 'success' });
    } else {
      setToast({ message: 'Please select a valid JSON file', type: 'error' });
    }
  };

  // Process mock data from file
  const processMockData = async () => {
    if (!mockDataFile) {
      setToast({ message: 'Please select a mock data file first', type: 'error' });
      return;
    }

    setAnalyzing(true);
    try {
      const fileContent = await mockDataFile.text();
      const mockResponse = JSON.parse(fileContent);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Process the mock response as if it came from the API
      return { success: true, data: mockResponse.data || mockResponse };
    } catch (error) {
      console.error('Error processing mock file:', error);
      setToast({ message: 'Failed to process mock data file', type: 'error' });
      return { success: false };
    }
  };

  // AI Analysis functionality
  const handleAnalyzeImages = async (useMockData = false) => {
    // Track which images to use for AI analysis
    let imagesToAnalyze: ProductImage[] = [];

    // In edit mode with existing images but no new uploads, fetch and download existing images
    if (!useMockData && isEditMode && productId) {
      // Images ready for analysis: either has file (local upload) or temp_path (Google Drive import)
      const uploadedImages = images.filter(img => img.url && (img.file || img.temp_path));

      // If no new uploads but we have existing images, download them from server
      if (uploadedImages.length === 0 && images.some(img => img.url)) {
        console.log('📥 No new uploads detected - fetching existing images for analysis');

        try {
          // Fetch current product images from backend
          const imageResponse = await productsApi.getById(productId);
          if (!imageResponse.success || !imageResponse.data) {
            throw new Error('Failed to load product images');
          }

          const productImages = imageResponse.data.images || [];
          console.log('Fetched product images:', productImages);

          if (productImages.length === 0) {
            setToast({ message: 'No images found for this product', type: 'error' });
            return;
          }

          // Download images from Square/Drive URLs via backend proxy (CORS workaround)
          const imageBlobs = await Promise.all(
            productImages.map(async (img: any) => {
              const originalUrl = img.square_url || img.google_drive_url || img.url;
              if (!originalUrl) {
                console.warn('Image has no URL:', img);
                return null;
              }

              try {
                // Use backend proxy to avoid CORS issues with Square's S3 bucket
                const proxyUrl = `/api/square/proxy-image?url=${encodeURIComponent(originalUrl)}`;
                const response = await fetch(proxyUrl, { credentials: 'include' });

                if (!response.ok) {
                  console.error(`Failed to fetch image from ${originalUrl}:`, response.status);
                  return null;
                }

                const blob = await response.blob();
                const filename = img.filename || `image_${img.type || 'unknown'}.jpg`;

                return {
                  id: img.id || `temp-${Date.now()}-${Math.random()}`,
                  file: new File([blob], filename, { type: blob.type || 'image/jpeg' }),
                  type: img.type || 'front',
                  url: originalUrl,
                  label: img.type || 'front'
                };
              } catch (error) {
                console.error(`Error downloading image from ${originalUrl}:`, error);
                return null;
              }
            })
          );

          // Filter out null values
          imagesToAnalyze = imageBlobs.filter(img => img !== null) as ProductImage[];

          if (imagesToAnalyze.length === 0) {
            setToast({ message: 'Failed to download any product images', type: 'error' });
            return;
          }

          console.log(`✅ Downloaded ${imagesToAnalyze.length} images from server for analysis`);
        } catch (error: any) {
          console.error('Failed to fetch existing images:', error);
          setToast({ message: `Failed to load images: ${error.message}`, type: 'error' });
          return;
        }
      } else if (uploadedImages.length === 0) {
        setToast({ message: 'Please upload at least one image to analyze', type: 'error' });
        return;
      } else {
        // Use uploaded images - they already have all required properties
        imagesToAnalyze = uploadedImages;
      }
    } else if (!useMockData) {
      // Images ready for analysis: either has file (local upload) or temp_path (Google Drive import)
      const uploadedImages = images.filter(img => img.url && (img.file || img.temp_path));
      if (uploadedImages.length === 0) {
        setToast({ message: 'Please upload at least one image to analyze', type: 'error' });
        return;
      }
      imagesToAnalyze = uploadedImages;
    }

    setAnalyzing(true);
    try {
      // Reload AI attributes before processing the response
      // This ensures we have the latest attribute definitions even if the initial load failed
      let currentAttributeDefs = aiAttributeDefinitions;
      console.log(`🔍 Initial aiAttributeDefinitions length: ${aiAttributeDefinitions.length}`);

      if (organization && (!aiAttributeDefinitions || aiAttributeDefinitions.length === 0)) {
        console.log('🔄 Reloading AI attributes before processing AI response...');
        console.log(`   Organization: ${organization.name} (${organization.id})`);

        const attrResponse = await aiAttributesApi.list({
          organization_id: organization.id,
          is_active: true
        });

        console.log('📡 Attribute reload response:', attrResponse);

        if (attrResponse.success && attrResponse.data) {
          currentAttributeDefs = attrResponse.data;
          setAiAttributeDefinitions(currentAttributeDefs);
          console.log(`✅ Reloaded ${currentAttributeDefs.length} AI attribute definitions`);
          if (currentAttributeDefs.length > 0) {
            console.log('📋 First 3 attributes:', currentAttributeDefs.slice(0, 3).map(d => ({
              key: d.attribute_key,
              scope: d.attribute_scope,
              type: d.attribute_type
            })));
          }
        } else {
          console.error('❌ Failed to reload attributes:', attrResponse.message || 'Unknown error');
        }
      } else {
        console.log(`✅ Using existing ${currentAttributeDefs.length} attribute definitions`);
      }

      const response = useMockData && mockDataFile
        ? await processMockData()
        : await aiApi.executeAIAnalysis(imagesToAnalyze);
      if (response.success && response.data) {
        setAiExecuteResult(response.data);
        const data = response.data;

        // Process dynamic AI response based on user-defined attributes
        console.log('=== AI RESPONSE PROCESSING ===');
        console.log('Full response data:', data);
        console.log('AI Attribute Definitions:', currentAttributeDefs);
        
        // The response data itself contains the AI fields directly
        // It's already been transformed by transformAIResponse in api.ts
        const aiResponse = data;
        
        if (aiResponse && Object.keys(aiResponse).length > 0) {
          // Debug log the AI response
          console.log('Using AI Response:', aiResponse);
          console.log('Has square_projection?', !!aiResponse.square_projection);
          console.log('Has internal_record?', !!aiResponse.internal_record);

          // AI-powered image reordering based on classification
          if (aiResponse.image_order && typeof aiResponse.image_order === 'object') {
            const imageOrder: Record<string, string> = aiResponse.image_order;
            console.log('🖼️ AI image_order classification:', imageOrder);

            const typeToPosition: Record<string, number> = { front: 0, back: 1, detail: 2 };
            const currentImages = [...images];
            const reordered: (typeof currentImages[0] | null)[] = new Array(currentImages.length).fill(null);
            const placed = new Set<number>();

            // Place each image into its AI-classified position
            Object.entries(imageOrder).forEach(([key, imageType]) => {
              const sourceIndex = parseInt(key.replace('image_', ''), 10) - 1;
              const targetIndex = typeToPosition[imageType];
              if (
                sourceIndex >= 0 && sourceIndex < currentImages.length &&
                targetIndex !== undefined && targetIndex < currentImages.length &&
                !placed.has(targetIndex) && currentImages[sourceIndex]?.url
              ) {
                reordered[targetIndex] = { ...currentImages[sourceIndex], order: targetIndex };
                placed.add(targetIndex);
              }
            });

            // Fill remaining slots with unplaced images
            const unplacedSources = currentImages.filter((_, i) =>
              !Object.entries(imageOrder).some(([key, imgType]) => {
                const si = parseInt(key.replace('image_', ''), 10) - 1;
                const ti = typeToPosition[imgType];
                return si === i && ti !== undefined && placed.has(ti);
              })
            );
            let unplacedIdx = 0;
            for (let i = 0; i < reordered.length; i++) {
              if (!reordered[i] && unplacedIdx < unplacedSources.length) {
                reordered[i] = { ...unplacedSources[unplacedIdx], order: i };
                unplacedIdx++;
              }
            }

            // Apply reordering if any images were actually moved
            const finalImages = reordered.map((img, i) => img || currentImages[i]);
            const wasReordered = finalImages.some((img, i) => img.url !== currentImages[i].url);
            if (wasReordered) {
              console.log('🔄 AI reordered images: front→0, back→1, detail→2');
              setImages(finalImages);
              setToast({ message: 'AI reordered images: front, back, detail', type: 'success' });
            }
          }
          
          // Create a new aiFields object dynamically based on attribute definitions
          const newAiFields: any = {};

          // Always populate standard AI fields (title, descriptions) even without custom definitions
          const squareProjection = aiResponse.square_projection || {};
          const internalRecord = aiResponse.internal_record || {};

          // Standard fields that don't require definitions
          if (squareProjection.product_title) {
            newAiFields.product_title = squareProjection.product_title;
            console.log('✅ Set product_title:', squareProjection.product_title);
          }
          if (squareProjection.market_description) {
            newAiFields.market_description = squareProjection.market_description;
            console.log('✅ Set market_description');
          }
          if (internalRecord.detailed_inventory_description) {
            newAiFields.detailed_inventory_description = internalRecord.detailed_inventory_description;
            console.log('✅ Set detailed_inventory_description');
          }

          // If we don't have attribute definitions, we can still populate the standard fields
          if (!currentAttributeDefs || currentAttributeDefs.length === 0) {
            console.warn('No AI attribute definitions - only populating standard fields (title, descriptions)');
            setFormData(prev => ({
              ...prev,
              name: squareProjection.product_title || prev.name,
              description: squareProjection.market_description || prev.description,
              aiFields: newAiFields
            }));
            // Note: No toast notification - progress overlay is sufficient
            return;
          }
          
          // Process each attribute scope
          console.log(`\n🔄 Starting attribute mapping with ${currentAttributeDefs.length} definitions`);
          console.log(`   Square projection keys available:`, Object.keys(squareProjection));
          console.log(`   Internal record keys available:`, Object.keys(internalRecord));
          console.log(`   Internal record has attributes_rich:`, !!internalRecord.attributes_rich);

          currentAttributeDefs.forEach(attrDef => {
            const key = attrDef.attribute_key;
            console.log(`\n📝 Processing attribute: ${key} (scope: ${attrDef.attribute_scope}, rich: ${!!attrDef.is_rich_attribute})`);

            // Look for the value in different places based on scope and response structure
            let value = null;

            // The response might be flat (from transformAIResponse) or nested (from backend)
            // First check if the key exists directly in the response (flat structure)
            const cleanKey = key.replace('k.', '');

            // Check nested structure first (preferred)
            if (aiResponse.square_projection || aiResponse.internal_record) {
              const squareProjection = aiResponse.square_projection || {};
              const internalRecord = aiResponse.internal_record || {};

              // NEW SCHEMA-BASED SCOPES: Handle square_projection and internal_record
              if (attrDef.attribute_scope === 'square_projection') {
                // Look for the value directly in square_projection
                if (squareProjection[key] !== undefined) {
                  value = squareProjection[key];
                  console.log(`Found value for ${key} in square_projection:`, value);
                }
              } else if (attrDef.attribute_scope === 'internal_record') {
                // Check if this is a rich attribute (from attributes_rich array)
                if (attrDef.is_rich_attribute && internalRecord.attributes_rich && Array.isArray(internalRecord.attributes_rich)) {
                  // Find the rich attribute by key
                  const richAttr = internalRecord.attributes_rich.find((a: any) => a.key === key);
                  if (richAttr) {
                    value = richAttr.value;
                    console.log(`Found rich attr value for ${key}:`, value);
                  } else {
                    console.log(`No rich attr found for ${key}. Available:`, internalRecord.attributes_rich.map((a: any) => a.key));
                  }
                } else {
                  // Regular internal_record attribute - look directly in internal_record
                  if (internalRecord[key] !== undefined) {
                    value = internalRecord[key];
                    console.log(`Found value for ${key} in internal_record:`, value);
                  }
                }
              }
              // LEGACY SCOPES: Keep old logic for backward compatibility
              else if (attrDef.attribute_scope === 'categories') {
                // Categories might be in square_projection.categories
                if (squareProjection.categories) {
                  value = Array.isArray(squareProjection.categories) ? squareProjection.categories[0] : squareProjection.categories;
                }
              } else if (attrDef.attribute_scope === 'custom_attributes') {
                // Custom attributes are in square_projection.custom_attributes
                if (squareProjection.custom_attributes) {
                  console.log(`Looking for ${key} in custom_attributes. Available keys:`, Object.keys(squareProjection.custom_attributes));

                  // First try mapping the AI response attributes to our Square keys
                  const mappedAttributes = mapAIAttributesToSquare(squareProjection.custom_attributes);
                  console.log('Mapped AI attributes to Square format:', mappedAttributes);

                  // Try with the full key first
                  if (mappedAttributes[key]) {
                    value = mappedAttributes[key];
                    console.log(`Found mapped value for ${key}:`, value);
                  }
                  // Try original keys as fallback
                  else if (squareProjection.custom_attributes[key]) {
                    value = squareProjection.custom_attributes[key];
                    console.log(`Found original value for ${key}:`, value);
                  }
                  // Try without k. prefix (since backend may strip it)
                  else if (squareProjection.custom_attributes[cleanKey]) {
                    value = squareProjection.custom_attributes[cleanKey];
                    console.log(`Found value for ${cleanKey}:`, value);
                  } else {
                    console.log(`WARNING: Attribute ${key} not found in AI response after mapping. Available mapped keys:`, Object.keys(mappedAttributes));
                  }
                }
              } else if (attrDef.attribute_scope === 'attributes_rich') {
                // Rich attributes are in internal_record.attributes_rich array
                if (internalRecord.attributes_rich && Array.isArray(internalRecord.attributes_rich)) {
                  // Normalize the database attribute key for comparison (Pattern_Classification -> pattern.classification)
                  const normalizedDbKey = key.replace(/_/g, ' ').toLowerCase();

                  // First try to match with the attribute_key directly
                  let richAttr = internalRecord.attributes_rich.find((a: any) => a.key === key);

                  // If not found, try matching with ai_prompt_key
                  if (!richAttr && attrDef.ai_prompt_key) {
                    richAttr = internalRecord.attributes_rich.find((a: any) => a.key === attrDef.ai_prompt_key);
                    console.log(`Looking for rich attr with ai_prompt_key "${attrDef.ai_prompt_key}":`, richAttr);
                  }

                  // If still not found, try case-insensitive match with underscores replaced by spaces
                  if (!richAttr) {
                    richAttr = internalRecord.attributes_rich.find((a: any) => {
                      const aiKeyNormalized = a.key.toLowerCase();
                      const match = aiKeyNormalized === normalizedDbKey;
                      console.log(`Comparing "${a.key}" (normalized: "${aiKeyNormalized}") to "${key}" (normalized: "${normalizedDbKey}"): ${match}`);
                      return match;
                    });
                  }

                  // If still not found, try mapping the rich attr key using normalizeAttributeKey
                  if (!richAttr) {
                    richAttr = internalRecord.attributes_rich.find((a: any) => {
                      // Try to map the AI key (a.key) to see if it matches our attribute_key
                      const normalizedKey = normalizeAttributeKey(a.key);
                      console.log(`Normalized "${a.key}" to "${normalizedKey}", comparing to "${key}"`);
                      return normalizedKey === key;
                    });
                  }

                  if (richAttr) {
                    value = richAttr.value;
                    console.log(`Found rich attr value for ${key}:`, value);
                  } else {
                    console.log(`No rich attr found for ${key}. Available rich attrs:`, internalRecord.attributes_rich.map((a: any) => a.key));
                  }
                }
              } else if (attrDef.attribute_scope === 'internal_attributes') {
                // Internal attributes might be directly in internal_record
                if (internalRecord[key]) {
                  value = internalRecord[key];
                }
              }
            } else {
              // Fallback to direct access if no nested structure
              if (aiResponse[key] !== undefined) {
                value = aiResponse[key];
                console.log(`Found value for ${key} directly:`, value);
              } else if (aiResponse[cleanKey] !== undefined) {
                value = aiResponse[cleanKey];
                console.log(`Found value for ${cleanKey} directly:`, value);
              }
            }
            
            // Handle array values properly
            if (Array.isArray(value)) {
              if (attrDef.attribute_type === 'enum') {
                value = value[0]; // Take first for single select
              } else if (attrDef.attribute_type === 'multi_select') {
                value = value.join(','); // Join for multi-select
              }
            }
            
            // Also check common field mappings
            if (!value && attrDef.attribute_scope === 'internal_attributes') {
              const internalFieldMap: Record<string, string> = {
                'garment_type': aiResponse.garment_type,
                'period': aiResponse.period,
                'condition': aiResponse.condition,
                'fabric_type': internalRecord.fabric_type,
                'technique': internalRecord.technique,
              };
              if (internalFieldMap[key]) {
                value = internalFieldMap[key];
              }
            }
            
            // Store the value if found
            if (value !== null && value !== undefined) {
              newAiFields[key] = value;
              console.log(`   ✅ Setting ${key} = ${value}`);
            } else {
              console.log(`   ❌ No value found for ${key}`);
            }
          });

          console.log(`\n🎯 Mapping complete: ${Object.keys(newAiFields).length} fields mapped`);
          console.log('   Mapped fields:', Object.keys(newAiFields));
          
          // The product title and description come from square_projection
          // These are standard fields that the AI always returns
          let productTitle = '';
          let marketDescription = '';
          let detailedDescription = '';
          
          if (aiResponse.square_projection) {
            console.log('=== square_projection content ===', aiResponse.square_projection);
            if (aiResponse.square_projection.product_title) {
              productTitle = aiResponse.square_projection.product_title;
              console.log('Found product title:', productTitle);
            }
            if (aiResponse.square_projection.market_description) {
              marketDescription = aiResponse.square_projection.market_description;
              console.log('Found market description:', marketDescription);
            }
            if (aiResponse.square_projection.detailed_inventory_description) {
              detailedDescription = aiResponse.square_projection.detailed_inventory_description;
              console.log('Found detailed description in square_projection:', detailedDescription);
            } else {
              console.log('WARNING: detailed_inventory_description not found in square_projection');
              console.log('Available keys in square_projection:', Object.keys(aiResponse.square_projection));
            }
          }
          
          // Check if detailed_inventory_description might be in internal_record
          if (!detailedDescription && aiResponse.internal_record) {
            if (aiResponse.internal_record.detailed_inventory_description) {
              detailedDescription = aiResponse.internal_record.detailed_inventory_description;
              console.log('Found detailed description in internal_record:', detailedDescription);
            } else {
              console.log('detailed_inventory_description also not in internal_record');
              console.log('Available keys in internal_record:', Object.keys(aiResponse.internal_record));
            }
          }
          
          // Check if it might be at the top level
          if (!detailedDescription && aiResponse.detailed_inventory_description) {
            detailedDescription = aiResponse.detailed_inventory_description;
            console.log('Found detailed description at top level:', detailedDescription);
          }
          
          const newFormData = {
            name: productTitle || formData.name,
            description: marketDescription || formData.description,
            sku: formData.sku || data.sku || extractSkuFromImages(images),
            aiFields: {
              ...formData.aiFields,
              ...newAiFields,
              product_title: productTitle,
              market_description: marketDescription,
              detailed_inventory_description: detailedDescription,
            },
          };
          
          console.log('Setting new form data:', newFormData);
          console.log('aiFields being set:', newFormData.aiFields);

          // Update form data FIRST before any async operations
          // Clear categories array since AI analysis starts fresh
          setFormData(prev => {
            const updated = {
              ...prev,
              ...newFormData,
              categories: [] // Clear additional categories when AI runs
            };
            console.log('Updated full formData:', updated);
            console.log('Updated aiFields:', updated.aiFields);
            console.log('Cleared categories array for fresh AI analysis');
            console.log('Specifically detailed_inventory_description:', updated.aiFields.detailed_inventory_description);
            return updated;
          });
          

          // Note: Classification categories are NOT added to formData.categories here.
          // They are stored in formData.aiFields (category, gender, occasion, etc.)
          // and will be processed during save via ensureCategoriesExist()
          // The categories array is only for manually selected Additional Square Categories
        }

        // Note: No toast notification on success - progress overlay is sufficient
      } else {
        console.error('AI analysis failed:', response.message);
        setToast({ message: response.message || 'AI analysis failed', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to analyze images:', error);
      setToast({ message: 'Failed to analyze images. Please try again.', type: 'error' });
    } finally {
      setAnalyzing(false);
    }
  };


  // Helper function to map audience to gender
  const mapAudienceToGender = (audience: string | undefined): string => {
    if (!audience) return '';
    switch (audience) {
      case 'MEN': return 'Men';
      case 'WOMEN': return 'Women';
      case 'CHILD': return 'Child_Unisex';
      case 'UNISEX': return 'Unisex';
      default: return audience;
    }
  };

  // Convert AI response to custom attributes - unused for now
  // const convertAIResponseToCustomAttributes = (aiResponse: AIExecuteResponse) => {
  //   const customAttrs: Array<{id: string; name: string; value: string; isEditable: boolean}> = [];
  //   
  //   const predefinedAiFieldKeys = [
  //     'category', 'gender', 'size_category', 'occasion', 'season', 'primary_color',
  //     'era', 'condition', 'product_title', 'market_description', 'detailed_inventory_description'
  //   ];
  //   
  //   if (aiResponse.attributes) {
  //     Object.entries(aiResponse.attributes).forEach(([key, value]) => {
  //       if (!predefinedAiFieldKeys.includes(key) && value && typeof value === 'string') {
  //         customAttrs.push({
  //           id: `ai_${key}_${Date.now()}`,
  //           name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
  //           value: value,
  //           isEditable: true,
  //         });
  //       }
  //     });
  //   }
  //   
  //   return customAttrs;
  // };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validate required fields
      if (!formData.name.trim() && !formData.aiFields.product_title.trim()) {
        throw new Error('Product name or title is required');
      }
      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Valid price is required');
      }

      // Upload images to Square first
      let uploadedImageIds: string[] = [];

      // Sort images by their order field to ensure proper ordering
      // Image at position 0 becomes the primary/featured image in Square
      const sortImagesByOrder = (imgs: ProductImage[]) => {
        return [...imgs].sort((a, b) => {
          const aOrder = a.order ?? 999;
          const bOrder = b.order ?? 999;
          return aOrder - bOrder;
        });
      };

      // Separate images into those with files vs temporary files, maintaining upload order
      const sortedImages = sortImagesByOrder(images);
      const newImages = sortedImages.filter(img => img.file);
      const tempImages = sortedImages.filter(img => img.temp_path && !img.file);

      // Upload temporary files from Google Drive first
      if (tempImages.length > 0) {
        console.log('Uploading temporary files to Square...', tempImages.length);
        try {
          const tempFilesData = tempImages.map(img => ({
            filename: img.filename,
            original_name: img.original_name || img.label,
            drive_file_id: img.drive_file_id,
            temp_path: img.temp_path
          }));

          const tempUploadResponse = await productsApi.uploadTempFilesToSquare(tempFilesData);

          if (tempUploadResponse.success && tempUploadResponse.data?.image_ids) {
            uploadedImageIds = [...uploadedImageIds, ...tempUploadResponse.data.image_ids];
            console.log('Successfully uploaded temporary files:', tempUploadResponse.data.image_ids);
          } else {
            throw new Error(tempUploadResponse.message || 'Failed to upload temporary files');
          }
        } catch (error) {
          console.error('Error uploading temporary files:', error);
          setToast({
            message: 'Failed to upload temporary files. Will continue with other images.',
            type: 'error'
          });
        }
      }

      // Upload new images with File objects
      if (newImages.length > 0) {
        console.log('📤 Uploading images to Square in order:', newImages.map((img, idx) => ({
          order: img.order,
          index: idx,
          filename: img.file?.name,
          type: img.type
        })));

        try {
          const formData = new FormData();
          // Use indexed keys to guarantee order preservation (image_0, image_1, image_2)
          newImages.forEach((img, index) => {
            if (img.file) {
              formData.append('images', img.file);
              console.log(`  [${index}] order=${img.order} file=${img.file.name}`);
            }
          });

          // Attach Bearer token for cross-subdomain auth — matches pattern used by
          // apiFileUploadRequest and productsApi.uploadTempFilesToSquare in services/api.ts.
          // Don't set Content-Type — browser auto-generates multipart boundary.
          const uploadAuthToken = localStorage.getItem('auth_token');
          if (!uploadAuthToken) {
            // Surface clear error instead of firing the upload anonymously and
            // hitting a generic 401. Caught by the outer catch.
            throw new Error('AUTH_MISSING');
          }
          const uploadHeaders: Record<string, string> = {
            Authorization: `Bearer ${uploadAuthToken}`,
          };

          const uploadResponse = await fetch('/api/square/images/upload', {
            method: 'POST',
            credentials: 'include',
            headers: uploadHeaders,
            body: formData
          });

          if (!uploadResponse.ok) {
            if (uploadResponse.status === 401) {
              throw new Error('AUTH_EXPIRED');
            }
            throw new Error(`Image upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }

          const uploadResult = await uploadResponse.json();
          if (uploadResult.success && uploadResult.image_ids) {
            uploadedImageIds = [...uploadedImageIds, ...uploadResult.image_ids];
            console.log('Successfully uploaded images:', uploadResult.image_ids);
          } else {
            throw new Error('Failed to get image IDs from upload response');
          }
        } catch (error) {
          console.error('Error uploading images:', error);
          const errMsg = error instanceof Error ? error.message : '';
          if (errMsg === 'AUTH_MISSING' || errMsg === 'AUTH_EXPIRED') {
            // Auth failures must not silently fall through into "create product
            // without images" — that produces an orphaned product. Stop the save.
            setToast({
              message: 'Your session has expired. Please sign in again to upload images.',
              type: 'error'
            });
            setSaving(false);
            return;
          }
          setToast({
            message: 'Failed to upload images. Creating product without images.',
            type: 'error'
          });
          // Continue without images rather than failing completely
        }
      }

      // Prepare custom attributes for creation
      // TODO: Implement unified attributes processing
      const customAttrsToCreate: Array<{name: string; value: string}> = [];

      const createdCustomAttributes: Array<{attributeId: string; value: string}> = [];
      
      if (customAttrsToCreate.length > 0) {
        const currentAttributesResponse = await attributesApi.getAll();
        if (currentAttributesResponse.success && currentAttributesResponse.data) {
          const existingAttributes = currentAttributesResponse.data;
          
          for (const customAttr of customAttrsToCreate) {
            const existingAttr = existingAttributes.find(attr => 
              attr.name.toLowerCase() === customAttr.name.toLowerCase()
            );
            
            if (existingAttr) {
              createdCustomAttributes.push({
                attributeId: existingAttr.id,
                value: customAttr.value
              });
            } else {
              const createAttrResponse = await attributesApi.create({
                name: customAttr.name,
                type: AttributeType.STRING,
                description: `Custom attribute: ${customAttr.name}`,
                allowed_object_types: ['ITEM']
              });
              
              if (createAttrResponse.success && createAttrResponse.data) {
                createdCustomAttributes.push({
                  attributeId: createAttrResponse.data.id,
                  value: customAttr.value
                });
              }
            }
          }
        }
      }

      // Create Square categories from classification values
      // Only create categories for customer-facing fields (category only)
      console.log('Creating Square categories for classifications...');
      const classificationCategoryIds = await ensureCategoriesExist({
        category: formData.aiFields.category || undefined,
        // gender field is deprecated - audience field is used instead for internal classification only
        // Other fields (era, season, occasion, condition, audience) stored as AI attributes only
      });
      console.log('Classification categories created/found:', classificationCategoryIds);

      // Combine with existing selected categories
      const allCategoryIds = [...new Set([...formData.categories, ...classificationCategoryIds])];

      // NOTE: Attributes are now stored in our database only (AIAnalysis table)
      // They are NOT sent to Square as custom attributes
      // All AI fields (era, condition, audience, etc.) are stored in the database
      // when the product is created/updated via the backend

      // Use product_title if provided, otherwise use name
      const productName = formData.aiFields.product_title || formData.name;

      // Square's description field gets market_description (max 4096 chars)
      // detailed_inventory_description is stored separately in our database (AIAnalysis table)
      const squareDescription = formData.aiFields.market_description || '';

      // Truncate if exceeds Square's limit
      const maxSquareDescriptionLength = 4096;
      const finalDescription = squareDescription.length > maxSquareDescriptionLength
        ? squareDescription.substring(0, maxSquareDescriptionLength - 3) + '...'
        : squareDescription;

      // Collect all image IDs (existing + newly uploaded)
      // Sort existing images by order to maintain proper sequence
      // SC-076: An existing image must have a real Square image_id AND either
      // a resolved url OR be flagged as `needsUrlResolution` (mid-resolution
      // from a partial backend response — see api.ts transformApiProduct).
      // Empty placeholder slots created by createImageSlots() also have an `id`
      // (e.g. "1", "2", "3") but no url and no resolution flag — those must
      // NOT be sent to Square or it returns 400 INVALID_VALUE for unknown
      // ITEM_IMAGE tokens. Dropping `needsUrlResolution` images would silently
      // unlink real images from the product, hence accepting them here.
      const existingImageIds = sortImagesByOrder(images)
        .filter((img: ProductImage) => img.id && (img.url || img.needsUrlResolution) && !img.file && !img.temp_path)
        .map((img: ProductImage) => img.id);

      // SC-076 followup (Wren MED-2): warn if an image with a real id slipped
      // past the file/temp_path checks but has neither url nor needsUrlResolution.
      // This shape shouldn't exist in practice — log it so future regressions
      // surface instead of silently dropping the user's images.
      const droppedReal = images.filter(
        (img: ProductImage) =>
          img.id &&
          !img.file &&
          !img.temp_path &&
          !(img.url || img.needsUrlResolution)
      );
      if (droppedReal.length > 0) {
        console.warn(
          'SC-076 followup: dropped images that look real but lacked url + needsUrlResolution',
          droppedReal.map((i) => ({ id: i.id }))
        );
      }

      const allImageIds = [...existingImageIds, ...uploadedImageIds];
      console.log('📷 Image IDs for product (ordered by position):', {
        existing: existingImageIds,
        newlyUploaded: uploadedImageIds,
        total: allImageIds,
        primaryImage: allImageIds[0] // First image (order=0) is primary/featured
      });

      // Build AI attributes array from formData.aiFields
      // Convert AI fields to backend AttributeCreate format: { name: string, string_value: string }
      const aiAttributes: Array<{name: string; string_value: string}> = [];

      // Special fields that are stored separately
      const specialFields = ['product_title', 'market_description', 'detailed_inventory_description'];

      Object.entries(formData.aiFields).forEach(([key, value]) => {
        // Skip special fields (they go in separate fields)
        if (specialFields.includes(key)) {
          return;
        }

        // Skip empty values
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return;
        }

        // Convert arrays to comma-separated strings
        const stringValue = Array.isArray(value) ? value.join(', ') : String(value);

        aiAttributes.push({
          name: key,
          string_value: stringValue
        });
      });

      console.log('📋 Sending AI attributes to backend:', aiAttributes);

      // Build the product request
      const productRequest: CreateProductRequest = {
        name: productName,
        description: finalDescription,
        sku: formData.sku || '',
        price: parseFloat(formData.price),
        currency: 'USD',
        inventory_quantity: parseInt(formData.inventory_quantity) || 1,
        categories: allCategoryIds,  // Send full array of category IDs
        attributes: aiAttributes,  // Send AI attributes for database storage
        images: [], // Required by type
        image_ids: allImageIds.length > 0 ? allImageIds : undefined,
        // Add AI-generated description fields
        product_title: formData.aiFields.product_title || undefined,
        market_description: formData.aiFields.market_description || undefined,
        detailed_inventory_description: formData.aiFields.detailed_inventory_description || undefined,
        tags: [] // Required by type
      };

      let response;
      if (isEditMode && productId) {
        response = await productsApi.update(productId, productRequest);
      } else {
        response = await productsApi.create(productRequest);
      }
      
      if (response.success) {
        setToast({
          message: `Product ${isEditMode ? 'updated' : 'created'} successfully!`,
          type: 'success'
        });

        // For new products, show QR code modal before navigating away
        if (!isEditMode && response.data?.id) {
          setShowQRAfterSave({
            productName: formData.aiFields.product_title || formData.name || 'Product',
            productId: response.data.id
          });
          // Don't auto-navigate when showing QR - user will close modal to continue
        } else {
          // For edits, navigate back to products list after a delay
          setTimeout(() => {
            onNavigate('products');
          }, 2000);
        }
      } else {
        throw new Error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} product`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} product:`, error);

      // Extract error message
      let errorMessage = error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} product`;

      // Check if it's a duplicate error and provide user-friendly message
      if (errorMessage.toLowerCase().includes('already exists')) {
        errorMessage = `A product with the name "${formData.name.trim()}" already exists. Please choose a different name.`;
      }

      setToast({
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        <fieldset disabled={analyzing} className={analyzing ? 'opacity-75 pointer-events-none' : ''}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <Package className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {viewOnly ? 'View Product' : (isEditMode ? 'Edit Product' : 'Add Product')}
                </h1>
                <p className="text-gray-600">
                  {viewOnly ? 'Product details (read-only)' : (isEditMode ? 'Update product information' : 'Create a new product')}
                </p>
              </div>
            </div>
            {viewOnly && productId && (
              <Button
                type="button"
                onClick={() => onNavigate('product-add-edit', productId)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center gap-2"
              >
                <Edit className="w-5 h-5" />
                Edit Product
              </Button>
            )}
          </div>
          
          {/* Removed attribute count display for cleaner UI */}
        </div>

        {/* AI Analysis Results */}
        {aiExecuteResult && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">AI Analysis Complete</h3>
                <p className="text-sm text-green-600">
                  {aiExecuteResult.parsed_content?.internal_record?.confidence_level ? 
                    `Confidence: ${aiExecuteResult.parsed_content.internal_record.confidence_level}` :
                    aiExecuteResult.validated_response?.internal_record?.confidence_level ?
                      `Confidence: ${aiExecuteResult.validated_response.internal_record.confidence_level}` :
                    aiExecuteResult.confidence ? 
                      `Confidence: ${Math.round(aiExecuteResult.confidence * 100)}%` : 
                      'Analysis completed'
                  }
                  {aiExecuteResult.duration_ms && ` • Processing time: ${(aiExecuteResult.duration_ms / 1000).toFixed(2)}s`}
                </p>
              </div>
            </div>
            
            {/* Display SKU Context Note if available */}
            {(() => {
              const responseData = aiExecuteResult.parsed_content || aiExecuteResult.validated_response || {};
              const skuNote = responseData.sku_context_note || responseData.square_projection?.sku_context_note;
              
              if (skuNote) {
                return (
                  <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <p className="text-sm text-blue-700">
                      <strong>SKU Context:</strong> {skuNote}
                    </p>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Display Rich Attributes if available */}
            {(() => {
              const responseData = aiExecuteResult.parsed_content || aiExecuteResult.validated_response || {};
              const internalRecord = responseData.internal_record || {};
              const richAttrs = internalRecord.attributes_rich || [];
              
              if (richAttrs.length > 0) {
                return (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Detailed Attributes Detected:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {richAttrs.map((attr, idx) => (
                        <div key={idx} className="bg-white px-2 py-1 rounded text-xs">
                          <span className="font-medium text-gray-700">{attr.key.replace(/_/g, ' ')}:</span>
                          <span className="ml-1 text-gray-600">{attr.value.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            <p className="text-sm text-green-700 mt-4">
              Product details have been updated with AI suggestions. Review and modify as needed.
            </p>

            {/* AI Reasoning Section (Expandable Notes) */}
            {(() => {
              const responseData = aiExecuteResult.parsed_content || aiExecuteResult.validated_response || {};
              const hasNotes = responseData.square_notes || responseData.internal_notes;

              if (hasNotes) {
                const [showReasoning, setShowReasoning] = React.useState(false);

                return (
                  <div className="mt-4 border-t border-green-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowReasoning(!showReasoning)}
                      className="flex items-center justify-between w-full text-left text-sm font-semibold text-green-800 hover:text-green-900 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        AI Reasoning & Analysis Details
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${showReasoning ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showReasoning && (
                      <div className="mt-3 space-y-4">
                        {/* Square Data Notes */}
                        {responseData.square_notes && Object.keys(responseData.square_notes).length > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-green-100">
                            <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
                              Product Information Reasoning
                            </h5>
                            <div className="space-y-2">
                              {Object.entries(responseData.square_notes).map(([key, note]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-900">{key.replace(/_/g, ' ')}:</span>
                                  <span className="ml-2 text-gray-600">{note as string}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Internal Data Notes */}
                        {responseData.internal_notes && Object.keys(responseData.internal_notes).length > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-green-100">
                            <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
                              Internal Analysis Reasoning
                            </h5>
                            <div className="space-y-2">
                              {Object.entries(responseData.internal_notes).map(([key, note]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-900">{key.replace(/_/g, ' ')}:</span>
                                  <span className="ml-2 text-gray-600">{note as string}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Image Upload Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h2 className="text-xl font-semibold text-gray-900">Product Images</h2>
              </div>
              
              {!viewOnly && (
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    onClick={() => handleAnalyzeImages(false)}
                    disabled={analyzing || images.filter(img => img.url).length === 0}
                    className={`px-6 py-3 rounded-xl font-medium flex items-center gap-3 transition-all ${
                      analyzing
                        ? 'bg-purple-500 text-white cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700 text-white hover:shadow-lg'
                    }`}
                  >
                    <Sparkles className={`w-5 h-5 ${analyzing ? 'animate-spin' : ''}`} />
                    {analyzing ? loadingMessages[currentLoadingMessage] : 'AI Analyze Images'}
                  </Button>

                  {/* Development Mode Mock Data */}
                  {isDevelopment && (
                    <>
                      <div className="h-10 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <label className="relative cursor-pointer bg-amber-100 hover:bg-amber-200 text-amber-800 px-4 py-3 rounded-xl font-medium transition-all">
                          <input
                            type="file"
                            accept="application/json"
                            onChange={handleMockFileUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <span className="flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            {mockDataFile ? mockDataFile.name.substring(0, 20) + '...' : 'Load Mock JSON'}
                          </span>
                        </label>
                        {mockDataFile && (
                          <Button
                            type="button"
                            onClick={() => handleAnalyzeImages(true)}
                            disabled={analyzing}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-3 rounded-xl font-medium"
                          >
                            Use Mock Data
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="relative">
              <MultiImageUpload
                images={images.filter(img => ['front', 'back', 'detail', 'image'].includes(img.type)).map(img => ({
                  id: img.id,
                  type: img.type as 'front' | 'back' | 'detail' | 'image',
                  label: img.label,
                  url: img.url,
                  file: img.file,
                  order: img.order, // Preserve upload order (0 = primary)
                  // Preserve temporary file metadata from Google Drive imports
                  temp_path: img.temp_path,
                  drive_file_id: img.drive_file_id,
                  original_name: img.original_name,
                  filename: img.filename
                }))}
                onImagesChange={handleImagesChange}
                maxWidth={600}
                maxHeight={600}
                quality={0.7}
                disabled={viewOnly}
                minImages={minRequiredImages}
              />
              
              {/* AI Analysis Overlay */}
              {analyzing && (
                <div className="absolute inset-0 bg-purple-900/20 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                  <div className="bg-white/95 rounded-2xl p-8 shadow-2xl border border-purple-200 max-w-sm mx-auto text-center">
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative">
                        <Sparkles className="w-12 h-12 text-purple-600 animate-spin" />
                        <div className="absolute inset-0 w-12 h-12 border-4 border-purple-200 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-purple-900 mb-2">AI Analysis in Progress</h3>
                    <p className="text-purple-700 text-lg font-medium mb-4">
                      {loadingMessages[currentLoadingMessage]}
                    </p>
                    <div className="flex justify-center space-x-1">
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Product Details</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Title *
                </label>
                <input
                  type="text"
                  name="aiFields.product_title"
                  value={formData.aiFields.product_title || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="e.g., Vintage Navy Silk Kimono with Cherry Blossom Pattern"
                  required
                  disabled={viewOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Market Description
                </label>
                <textarea
                  name="aiFields.market_description"
                  value={formData.aiFields.market_description || ''}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Customer-focused description highlighting visual appeal, cultural significance, and wearing occasions..."
                  disabled={viewOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Inventory Description
                </label>
                <textarea
                  name="aiFields.detailed_inventory_description"
                  value={formData.aiFields.detailed_inventory_description || ''}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Technical details about construction, measurements, condition specifics..."
                  disabled={viewOnly}
                />
              </div>

              {/* Primary Category (from AI classification) */}
              <div>
                <label htmlFor="primary-category" className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Category
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Main product category. Changes here will update the Category field in AI Classification below.
                </p>
                <select
                  id="primary-category"
                  name="aiFields.category"
                  value={formData.aiFields.category || ''}
                  onChange={handleInputChange}
                  disabled={viewOnly}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select category...</option>
                  {[...(aiAttributeDefinitions?.find(attr => attr.attribute_key === 'category')?.possible_values || [])].sort((a, b) => a.localeCompare(b)).map(value => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>

              {/* Additional Square Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Square Categories
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select additional categories from your Square catalog (optional). The primary category above is automatically excluded.
                </p>
                <MultiCategorySelect
                  selectedCategories={formData.categories}
                  onChange={(categories) =>
                    setFormData(prev => ({ ...prev, categories }))
                  }
                  excludeCategories={[
                    // Exclude categories created from AI classification fields (category only)
                    ...(formData.aiFields.category ? [formData.aiFields.category] : []),
                  ]}
                  disabled={viewOnly}
                />
              </div>
            </div>
          </div>

          {/* Pricing & Inventory */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Tag className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Pricing & Inventory</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU
                </label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Product SKU"
                  disabled={viewOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                  required
                  disabled={viewOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inventory Quantity
                </label>
                <input
                  type="number"
                  min="0"
                  name="inventory_quantity"
                  value={formData.inventory_quantity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="1"
                  disabled={viewOnly}
                />
              </div>
            </div>
          </div>

          {/* Product Categories - Dynamic AI Fields */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Tags className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">AI Classification Fields</h2>
            </div>

            {/* Use dynamic AI fields based on user configuration */}
            {aiAttributesLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-500">Loading AI attributes...</div>
              </div>
            ) : (() => {
              console.log('🎨 Rendering DynamicAIFields with values:', formData.aiFields);
              console.log('🎨 Category value:', formData.aiFields?.category);
              return (
              <DynamicAIFields
                attributeDefinitions={aiAttributeDefinitions}
                values={formData.aiFields}
                onChange={(key, value) => {
                  console.log(`🔧 DynamicAIFields onChange: ${key} = ${value}`);
                  console.log(`🔍 Current formData.aiFields at onChange:`, formData.aiFields);
                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      aiFields: {
                        ...prev.aiFields,
                        [key]: value
                      }
                    };
                    console.log('Updated formData from onChange:', updated);
                    return updated;
                  });
                }}
                disabled={analyzing || viewOnly}
              />
              );
            })()}
          </div>



          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              onClick={() => onNavigate('products')}
              className="px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-medium"
            >
              {viewOnly ? 'Back to Products' : 'Cancel'}
            </Button>
            {!viewOnly && (
              <Button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Product' : 'Create Product')}
              </Button>
            )}
          </div>
        </form>

        </fieldset>

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={true}
            onClose={() => setToast(null)}
          />
        )}

        {/* QR Code Modal - shown after first save */}
        {showQRAfterSave && organization && (
          <QRCodeModal
            isOpen={true}
            onClose={() => {
              setShowQRAfterSave(null);
              onNavigate('products');
            }}
            productName={showQRAfterSave.productName}
            tenantSlug={organization.slug || organization.name}
            squareItemId={showQRAfterSave.productId}
            organizationId={organization.id}
          />
        )}
      </div>
    </div>
  );
};